<template>
  <form class="feedback-form" @submit.prevent="submit" novalidate>
    <div class="field">
      <label for="feedback-name">{{ $t("home.feedback.nameLabel") }}</label>
      <input
        id="feedback-name"
        v-model="name"
        type="text"
        autocomplete="name"
        maxlength="200"
        :placeholder="$t('home.feedback.namePlaceholder')"
        :disabled="status === 'submitting'"
      />
    </div>

    <div class="field">
      <label for="feedback-email">{{ $t("home.feedback.emailLabel") }}</label>
      <input
        id="feedback-email"
        v-model="email"
        type="email"
        autocomplete="email"
        maxlength="320"
        :placeholder="$t('home.feedback.emailPlaceholder')"
        :disabled="status === 'submitting'"
        :aria-invalid="errors.email ? 'true' : 'false'"
        :aria-describedby="errors.email ? 'feedback-email-error' : undefined"
        @input="clearError('email')"
        @blur="validateEmail"
      />
      <p
        v-if="errors.email"
        id="feedback-email-error"
        class="field-error"
        role="alert"
      >
        {{ errors.email }}
      </p>
    </div>

    <div class="field">
      <label for="feedback-message">
        {{ $t("home.feedback.messageLabel") }}
        <span class="required" aria-hidden="true">*</span>
      </label>
      <textarea
        id="feedback-message"
        v-model="message"
        required
        maxlength="2900"
        rows="5"
        :placeholder="$t('home.feedback.messagePlaceholder')"
        :disabled="status === 'submitting'"
        :aria-invalid="errors.message ? 'true' : 'false'"
        :aria-describedby="errors.message ? 'feedback-message-error' : undefined"
        @input="clearError('message')"
      ></textarea>
      <p
        v-if="errors.message"
        id="feedback-message-error"
        class="field-error"
        role="alert"
      >
        {{ errors.message }}
      </p>
    </div>

    <div class="honeypot" aria-hidden="true">
      <label for="feedback-hp">{{ $t("home.feedback.honeypotLabel") }}</label>
      <input
        id="feedback-hp"
        v-model="hpField"
        type="text"
        tabindex="-1"
        autocomplete="off"
      />
    </div>

    <button
      type="submit"
      class="submit-button"
      :disabled="status === 'submitting'"
    >
      {{
        status === "submitting"
          ? $t("home.feedback.submitting")
          : $t("home.feedback.submit")
      }}
    </button>

    <p
      v-if="status === 'success'"
      class="status-message status-success"
      role="status"
      aria-live="polite"
    >
      {{ $t("home.feedback.success") }}
    </p>
    <p
      v-if="status === 'error' && errorMessage"
      class="status-message status-error"
      role="alert"
      aria-live="assertive"
    >
      {{ errorMessage }}
    </p>
  </form>
</template>

<script>
import { cloudFunctions } from "@/store/cloudFunctions.js";
import { authStore } from "@/store/auth.js";

// AbortSignal.timeout() isn't available on iOS 15.0-15.3 WKWebView (the app's
// current minimum deployment target), where it would throw before fetch is
// ever called. Fall back to a manually-driven AbortController there.
function timeoutSignal(ms) {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

export default {
  name: "FeedbackForm",
  data() {
    return {
      name: "",
      email: "",
      message: "",
      hpField: "",
      status: "idle",
      errorMessage: "",
      errors: {
        email: "",
        message: "",
      },
    };
  },
  methods: {
    // Mirrors the backend's `_looksLikeEmail` check so the client and server
    // agree on what counts as a valid address.
    looksLikeEmail(value) {
      const trimmed = value.trim();
      const at = trimmed.indexOf("@");
      if (at === -1) return false;
      const local = trimmed.slice(0, at);
      const domain = trimmed.slice(at + 1);
      return Boolean(local) && domain.includes(".") && !domain.startsWith(".");
    },
    clearError(field) {
      this.errors[field] = "";
      // Dismiss a lingering server-error banner once the user starts fixing input.
      if (this.status === "error") {
        this.status = "idle";
        this.errorMessage = "";
      }
    },
    validateEmail() {
      const value = (this.email || "").trim();
      if (value && !this.looksLikeEmail(value)) {
        this.errors.email = this.$t("home.feedback.validationEmail");
      } else {
        this.errors.email = "";
      }
      return !this.errors.email;
    },
    validate() {
      this.errors.message = (this.message || "").trim()
        ? ""
        : this.$t("home.feedback.validationRequired");
      this.validateEmail();
      return !this.errors.message && !this.errors.email;
    },
    async submit() {
      if (this.status === "submitting") return;

      if (!this.validate()) {
        this.status = "error";
        this.errorMessage = "";
        return;
      }

      this.status = "submitting";
      this.errorMessage = "";

      try {
        const headers = { "Content-Type": "application/json" };
        try {
          const token = await authStore.getIdToken();
          if (token) headers.Authorization = `Bearer ${token}`;
        } catch {
          // Anonymous submission is fine; ignore token errors.
        }

        const response = await fetch(cloudFunctions.getFeedbackApi(), {
          method: "POST",
          headers,
          signal: timeoutSignal(10000),
          body: JSON.stringify({
            name: this.name,
            email: this.email,
            message: this.message,
            hpField: this.hpField,
          }),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        this.status = "success";
        this.name = "";
        this.email = "";
        this.message = "";
        this.errors.email = "";
        this.errors.message = "";
      } catch (err) {
        console.error("Feedback submit failed:", err);
        this.status = "error";
        this.errorMessage = this.$t("home.feedback.error");
      }
    },
  },
};
</script>

<style scoped>
.feedback-form {
  width: 100%;
  max-width: 600px;
  background-color: var(--al-white);
  border-radius: 10px;
  padding: 28px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  font-family: inherit;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--al-ink-900);
}

.required {
  color: var(--al-danger);
  margin-left: 2px;
}

.field input,
.field textarea {
  font: inherit;
  padding: 10px 12px;
  border: 1px solid var(--al-gray-300);
  border-radius: 6px;
  background-color: var(--al-white);
  color: var(--al-gray-700);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.field input::placeholder,
.field textarea::placeholder {
  color: var(--al-gray);
}

.field input:focus,
.field textarea:focus {
  outline: none;
  border-color: var(--al-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--al-primary) 20%, transparent);
}

.field input:disabled,
.field textarea:disabled {
  background-color: var(--al-surface-muted);
  cursor: not-allowed;
}

.field input[aria-invalid="true"],
.field textarea[aria-invalid="true"] {
  border-color: var(--al-danger);
}

.field input[aria-invalid="true"]:focus,
.field textarea[aria-invalid="true"]:focus {
  border-color: var(--al-danger);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--al-danger) 20%, transparent);
}

.field-error {
  margin: 0;
  font-size: 0.85rem;
  color: var(--al-danger);
}

.field textarea {
  resize: vertical;
  min-height: 100px;
}

.honeypot {
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.submit-button {
  align-self: flex-end;
  background-color: var(--al-primary);
  color: var(--al-white);
  border: none;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 700;
  font-family: inherit;
  border-radius: 6px;
  cursor: pointer;
  box-shadow: 0 4px 8px color-mix(in srgb, var(--al-primary) 30%, transparent);
  transition: background-color 0.2s ease, transform 0.1s ease;
  letter-spacing: 0.3px;
}

.submit-button:hover:not(:disabled),
.submit-button:focus-visible:not(:disabled) {
  background-color: var(--al-primary-hover);
}

.submit-button:active:not(:disabled) {
  transform: translateY(1px);
}

.submit-button:focus-visible {
  outline: 2px solid var(--al-primary-hover);
  outline-offset: 2px;
}

.submit-button:disabled {
  background-color: var(--al-gray-300);
  color: var(--al-gray-700);
  cursor: not-allowed;
  box-shadow: none;
}

.status-message {
  margin: 0;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 0.95rem;
}

.status-success {
  background-color: color-mix(in srgb, var(--al-success) 12%, var(--al-white));
  color: var(--al-ink-900);
  border: 1px solid color-mix(in srgb, var(--al-success) 35%, var(--al-white));
}

.status-error {
  background-color: var(--al-danger-bg);
  color: #842029; /* Alert danger convention from utilities.css, verified 7:1 contrast */
  border: 1px solid var(--al-danger-border);
}

@media (max-width: 767px) {
  .feedback-form {
    padding: 20px;
  }

  .submit-button {
    align-self: stretch;
  }
}
</style>
